import { customersApiClient } from './apiClient';

export const userService = {
  async getAccount() {
    const result = await customersApiClient.get('/account');

    if (!result.success) {
      return {
        success: false,
        data: null,
        error: result.error || 'Failed to fetch account',
      };
    }

    return {
      success: true,
      data: result.data?.user || null,
      error: null,
    };
  },

  async updateAccount(fields) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    const result = await customersApiClient.postFormData('/account', formData);

    if (!result.success) {
      return {
        success: false,
        data: null,
        error: result.error || 'Failed to update profile',
        raw: result.raw,
      };
    }

    return {
      success: true,
      data: result.data?.user || null,
      token: result.data?.token || null,
      error: null,
    };
  },

  async logout() {
    const result = await customersApiClient.post('/logout', {});
    return result;
  },

  async changePassword(currentPassword, newPassword, confirmPassword) {
    const result = await customersApiClient.postJson('/account/password', {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: confirmPassword,
    });

    if (!result.success) {
      return {
        success: false,
        data: null,
        error: result.error || 'Failed to change password',
        raw: result.raw,
      };
    }

    return {
      success: true,
      data: result.data,
      token: result.data?.token || null,
      error: null,
    };
  },

  async listEmails() {
    const result = await customersApiClient.get('/list-user-emails');
    if (!result.success) return {success: false, emails: [], error: result.error || 'Failed to fetch emails'};
    return {success: true, emails: result.data?.emails || [], error: null};
  },

  async addEmail(email) {
    const result = await customersApiClient.postJson('/add-user-email', {email});
    if (!result.success) return {success: false, error: result.error || 'Failed to add email', raw: result.raw, statusCode: result.statusCode};
    return {success: true, data: result.data, message: result.message, error: null};
  },

  async requestEmailOtp(email) {
    const result = await customersApiClient.postJson('/request-user-email-otp', {email});
    if (!result.success) return {success: false, error: result.error || 'Failed to send code', raw: result.raw, statusCode: result.statusCode};
    return {success: true, data: result.data, message: result.message, error: null};
  },

  async verifyEmailOtp(email, otp) {
    const result = await customersApiClient.postJson('/verify-user-email-otp', {email, otp});
    if (!result.success) return {success: false, error: result.error || 'Failed to verify code', raw: result.raw};
    return {success: true, data: result.data, error: null};
  },

  async setDefaultEmail(email) {
    const result = await customersApiClient.postJson('/set-user-email-default', {email});
    if (!result.success) return {success: false, error: result.error || 'Failed to set default', raw: result.raw};
    return {success: true, data: result.data, error: null};
  },

  /**
   * Add the email (and, for a placeholder profile, the name) of a Google / Apple account to the
   * SIGNED-IN account in one call — the provider already proved the email, so there is no code.
   * It lands verified, becomes the main email when the account has no verified main one (or when
   * `setAsDefault`), and the provider is linked for sign-in if the account has none linked yet.
   *
   * @param {'google'|'apple'} provider
   * @param {string} token  the provider's identity token (the one /auth/social takes)
   * @param {{setAsDefault?: boolean, authorizationCode?: string, firstName?: string, lastName?: string}} [opts]
   *   Apple only: firstName / lastName — Apple sends the name to the app on the first authorization only.
   * @returns {Promise<{success: boolean, user?: object, email?: object, linked?: boolean, error: string|null, raw?: object}>}
   *   On failure `raw.key` is social_email_missing | social_email_unverified | email_already_in_use | social_account_in_use.
   */
  async linkSocial(provider, token, {setAsDefault = false, authorizationCode, firstName, lastName} = {}) {
    const body = {token, set_as_default: setAsDefault};
    if (authorizationCode) body.authorization_code = authorizationCode;
    if (firstName) body.first_name = firstName;
    if (lastName) body.last_name = lastName;
    const result = await customersApiClient.postJson(`/account/link-social/${provider}`, body);
    if (!result.success) return {success: false, error: result.error || 'Could not link your account', raw: result.raw, statusCode: result.statusCode};
    return {success: true, user: result.data?.user || null, email: result.data?.email || null, linked: !!result.data?.linked, error: null};
  },

  async listPhones() {
    const result = await customersApiClient.get('/list-user-phones');
    if (!result.success) return {success: false, phones: [], error: result.error || 'Failed to fetch phones'};
    return {success: true, phones: result.data?.phones || [], error: null};
  },

  async addPhone(phone, countryCode = null) {
    const body = {phone};
    if (countryCode) body.country_code = countryCode;
    const result = await customersApiClient.postJson('/add-user-phone', body);
    if (!result.success) return {success: false, error: result.error || 'Failed to add phone', raw: result.raw, statusCode: result.statusCode};
    return {success: true, data: result.data, message: result.message, error: null};
  },

  async requestPhoneOtp(phone) {
    const result = await customersApiClient.postJson('/request-user-phone-otp', {phone});
    if (!result.success) return {success: false, error: result.error || 'Failed to send code', raw: result.raw, statusCode: result.statusCode};
    return {success: true, data: result.data, message: result.message, error: null};
  },

  async verifyPhoneOtp(phone, otp) {
    const result = await customersApiClient.postJson('/verify-user-phone-otp', {phone, otp});
    if (!result.success) return {success: false, error: result.error || 'Failed to verify code', raw: result.raw};
    return {success: true, data: result.data, error: null};
  },

  async setDefaultPhone(phone) {
    const result = await customersApiClient.postJson('/set-user-phone-default', {phone});
    if (!result.success) return {success: false, error: result.error || 'Failed to set default', raw: result.raw};
    return {success: true, data: result.data, error: null};
  },

  async listAddresses() {
    const result = await customersApiClient.get('/list-user-addresses');
    if (!result.success) return {success: false, addresses: [], error: result.error || 'Failed to fetch addresses'};
    return {success: true, addresses: result.data?.addresses || [], error: null};
  },

  async addAddress({label, notes, coordinate}) {
    const body = {coordinate};
    if (label) body.label = label;
    if (notes) body.notes = notes;
    const result = await customersApiClient.postJson('/add-user-address', body);
    if (!result.success) return {success: false, error: result.error || 'Failed to add address', raw: result.raw, statusCode: result.statusCode};
    return {success: true, data: result.data, message: result.message, error: null};
  },

};

export default userService;
